
import { WordPair } from "@/types/game";
import { checkWordsMatch } from "@/utils/gameUtils";
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel, captureStateSnapshot } from "@/utils/debugUtils";

// Import the refactored utilities
import { 
  processRoundCompletion,
  handleRoundOutcome
} from "@/utils/gameState/roundProcessors";

import {
  verifyRoundData,
  ensureRoundExists,
  verifyBothWordsExist
} from "@/utils/gameState/dataSync";

/**
 * Updates game state after a successful word submission
 */
export const updateGameStateAfterSubmission = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  aiExplanation: string | undefined,
  usedWords: Set<string>,
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setUsedWords: (usedWords: Set<string> | ((prev: Set<string>) => Set<string>)) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  toast: any,
  setIsSubmitting?: (isSubmitting: boolean) => void  // Added missing parameter
) => {
  // Signal that processing has started
  setProcessingComplete(false);
  
  debugLog(DebugLevel.INFO, "gameStateUpdates", `Starting updateGameStateAfterSubmission for round ${roundNumber}`, {
    userWord, 
    aiWord,
    hasExplanation: !!aiExplanation
  });
  
  captureStateSnapshot(
    "gameStateUpdates:start", 
    "updateGameStateAfterSubmission", 
    gameId, 
    roundNumber,
    userWord,
    aiWord,
    true, // userHasSubmitted
    true, // botHasChosenWord 
    false  // botIsThinking
  );

  // Step 1: Verify round data with the database
  const verifiedData = await verifyRoundData(gameId, roundNumber, userWord, aiWord, aiExplanation);
  userWord = verifiedData.userWord;
  aiWord = verifiedData.aiWord;
  aiExplanation = verifiedData.aiExplanation;

  // Step 2: Ensure the round exists in the database with both words
  const roundExists = await ensureRoundExists(gameId, roundNumber, userWord, aiWord, aiExplanation);
  if (!roundExists) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "There was a problem retrieving the round data."
    });
    setProcessingComplete(true);
    // Set submitting to false if the function is provided
    if (setIsSubmitting) setIsSubmitting(false);
    return;
  }

  // Step 3: Verify both words exist
  const wordsVerification = await verifyBothWordsExist(gameId, roundNumber, userWord, aiWord);
  if (!wordsVerification.success) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "There was a problem with the words for this round."
    });
    setProcessingComplete(true);
    // Set submitting to false if the function is provided
    if (setIsSubmitting) setIsSubmitting(false);
    return;
  }
  
  userWord = wordsVerification.userWord || userWord;
  aiWord = wordsVerification.aiWord || aiWord;
  aiExplanation = wordsVerification.aiExplanation || aiExplanation;

  debugLog(DebugLevel.INFO, "gameStateUpdates", `Final words for processing - User: ${userWord}, Bot: ${aiWord}`);
  
  // CRITICAL FIX: Force completion by setting critical state values directly 
  setBotHasChosenWord(true);
  setUserHasSubmitted(true);
  
  // Step 4: Process the current round completion 
  await processRoundCompletion(
    gameId,
    roundNumber,
    userWord,
    aiWord,
    aiExplanation,
    usedWords,
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setUserHasSubmitted,
    setProcessingComplete,
    toast
  );
  
  // Step 5: Handle the round outcome based on word comparison
  const wordsMatch = checkWordsMatch(userWord.toLowerCase(), aiWord);
  await handleRoundOutcome(
    gameId,
    roundNumber,
    userWord,
    aiWord,
    usedWords,
    wordsMatch,
    setRounds,
    setBotIsThinking,
    setBotHasChosenWord,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setProcessingComplete,
    toast
  );

  debugLog(DebugLevel.INFO, "gameStateUpdates", `Completed updateGameStateAfterSubmission for round ${roundNumber}`);
  captureStateSnapshot(
    "gameStateUpdates:end", 
    "completedUpdateGameState", 
    gameId, 
    roundNumber + 1,
    "", // New round has no user word yet
    "", // New round might have AI word already but we don't track it here
    false, // userHasSubmitted reset for next round
    false, // botHasChosenWord reset for next round
    false  // botIsThinking should be false or will be set in prepareNextRound
  );
};

// Re-export constants for backward compatibility
export { MAX_ROUNDS } from "@/utils/gameConstants";
